import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  const version = useRef(0);
  const setUser = useCallback((u) => { version.current += 1; setUserState(u); }, []);
  const refresh = useCallback(() => {
    const v = version.current;
    return api.get("/auth/me").then((r) => { if (v === version.current) setUserState(r.data); })
      .catch(() => { if (v === version.current) setUserState(false); });
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); setUser(false); };
  return <AuthCtx.Provider value={{ user, setUser, refresh, logout }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
export const isMember = (u) => !!u && (u.role === "admin" || u.has_access);

export function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center text-teal-700" data-testid="page-loader">
      <Loader2 className="h-7 w-7 animate-spin" aria-label="Loading" />
    </div>
  );
}

export function Protected({ children, admin = false, unlocked = false }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <PageLoader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (!admin && !isMember(user)) return <Navigate to="/login?reason=no-access" replace />;
  if (unlocked && user.locked) return <Navigate to="/dashboard" replace />;
  return children;
}
